/**
 * 数学工具
 */
export default class MathUtil {

    /**
    * 获取一个 min 到 max 范围内的随机整数
    * @param min 最小值
    * @param max 最大值
    */
    public static getRandomInt(min: number = 0, max: number = 1): number {
        return Math.floor(Math.random() * (max - min) + min);
    }

    /**
     * 获取一个伪随机整数
     * @param seed 随机种子
     * @param key key
     */
    public static getPseudoRandomInt(seed: number, key: number): number {
        return Math.ceil((((seed * 9301 + 49297) % 233280) / 233280) * key);
    }

    /**
     * 获取两点间的角度
     * @param p1 点1
     * @param p2 点2
     */
    public static getAngle(p1: cc.Vec2, p2: cc.Vec2): number {
        let radian = this.getRadian(p1, p2);
        return this.radianToAngle(radian);
    }

    /**
     * 获取两点间的角度 [-180, 180]
     * @param p1 点1
     * @param p2 点2
     */
    public static getAngle2(p1: cc.Vec2, p2: cc.Vec2): number {
        let radian = this.getRadian2(p1, p2);
        return this.radianToAngle(radian);
    }

    /**
     * 获取两点间的弧度
     * @param p1 点1
     * @param p2 点2
     */
     public static getRadian(p1: cc.Vec2, p2: cc.Vec2): number {
        return Math.atan((p2.y - p1.y) / (p2.x - p1.x));
    }

     /**
     * 获取两点间的弧度
     * @param p1 点1
     * @param p2 点2
     */
     public static getRadian2(p1: cc.Vec2, p2: cc.Vec2): number {
        return Math.atan2((p2.y - p1.y), (p2.x - p1.x));
    }

    /**
     * 已有点、距离和角度，求另一个位置
     * @param p1 
     * @param distance 
     * @param angle 
     * @returns 
     */
    public static getTargetPosFromAngleDistance(p1: cc.Vec2, distance: number, angle: number) {
        let d = cc.v2(distance, 0);
        let radian = this.angleToRadian(angle);
        let v = d.rotate(radian);
        return p1.add(v);
    }

    /**
     * 获取两点间的距离
     * @param p1 点1
     * @param p2 点2
     */
    public static getDistance(p1: cc.Vec2, p2: cc.Vec2): number {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    }

    /**
     * 将角度转为弧度
     * @param angle 角度
     */
    public static angleToRadian(angle: number): number {
        return angle * Math.PI / 180;
    }

    public static radianToAngle(radian: number): number {
        return radian * 180 / Math.PI;
    }

    /**
     * 浮点数加法运算（避免浮点数加法精度问题）
     * @param a 数
     * @param b 数
     */
    public static addSafely(a: number, b: number): number {
        const aDigits = (a.toString().split('.')[1] || '').length;
        const bDigits = (b.toString().split('.')[1] || '').length;
        const multiplier = Math.pow(10, Math.max(aDigits, bDigits));
        return (a * multiplier + b * multiplier) / multiplier;
    }

    /**
     * 一次贝塞尔即为线性插值函数
     * @param t 
     * @param posStart 
     * @param posEnd 
     * @returns 
     */
     public static bezierOne(t: number, posStart: cc.Vec2, posEnd: cc.Vec2): cc.Vec2 {
        if (t > 1) {
            t = 1;
        }
        else if (t < 0) {
            t = 0
        }

        var pStart: cc.Vec2 = posStart.clone();
        var pEnd: cc.Vec2 = posEnd.clone();

        return pStart.multiplyScalar(1 - t).add(pEnd.multiplyScalar(t));
    }

    /**
     * 二次贝塞尔曲线
     * @param t 
     * @param posStart 
     * @param posCon 
     * @param posEnd 
     * @returns 
     */
    public static bezierTwo(t: number, posStart: cc.Vec2, posCon: cc.Vec2, posEnd: cc.Vec2): cc.Vec2 {
        if (t > 1) {
            t = 1;
        }
        else if (t < 0) {
            t = 0
        }

        var n = (1 - t);
        var tt = t * t;

        var pStart: cc.Vec2 = posStart.clone();
        var pCon: cc.Vec2 = posCon.clone();
        var pEnd: cc.Vec2 = posEnd.clone();
        let pos = pStart.multiplyScalar(n * n);
        pos.addSelf(pCon.multiplyScalar(2 * n * t));
        pos.addSelf(pEnd.multiplyScalar(tt));
        return pos;
    }

    /**
     * 三次贝塞尔
     * @param t 
     * @param posStart 
     * @param posCon1 
     * @param posCon2 
     * @param posEnd 
     * @returns 
     */
    public static bezierThree(
        t: number
        , posStart: cc.Vec2
        , posCon1: cc.Vec2
        , posCon2: cc.Vec2
        , posEnd: cc.Vec2): cc.Vec2 {
        if (t > 1) {
            t = 1;
        }
        else if (t < 0) {
            t = 0
        }

        var n = (1 - t);
        var nn = n * n;
        var nnn = nn * n;
        var tt = t * t;
        var ttt = tt * t;

        var pStart: cc.Vec2 = posStart.clone();
        var pos = posStart.clone();

        var pCon1: cc.Vec2 = posCon1.clone();
        var pCon2: cc.Vec2 = posCon2.clone();
        var pEnd: cc.Vec2 = posEnd.clone();

        pos.add(pStart.multiplyScalar(nnn));
        pos.add(pCon1.multiplyScalar(3 * nn * t));
        pos.add(pCon2.multiplyScalar(3 * n * tt));
        pos.add(pEnd.multiplyScalar(ttt));

        return pos;
    }

}
